import React, {useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {selectExclusively} from '@mapsight/core/lib/feature-selections/actions';

import {
	createSelectionIndexSelector,
	listUiOptionSelectedOnlySelector,
	listUiOptionSelectionBehaviorSelector,
	listUiOptionSelectionSelectionSelector,
	listUiScrollToItemOnPreselectSelector,
	viewSelector
} from '../../store/selectors';
import createSelectFeature from '../feature-list-item/create-select-feature';

import {FEATURE_SELECTION_PRESELECT} from '../../config/feature/selections';
import {FEATURE_SELECTIONS} from '../../config/constants/controllers';

// TODO eine Comps.FeatureList und in feature-list/index.js wird diese Komponente genutzt statt dem herkömmlichem grouped-content.
// Projeke die den Keyboard-Handler auf der regulären Liste möchten, setzen dann in embed Comps.FeatureList auf withKeyboard(GroupedFeatureList)
// und feature-list/index.js nutzt dann Comps.FeatureList
// Achtung: als Eintrag in den Comp-Context funktioniert es aktuell nicht, weil der Zugriff aus connect() auf die Contexte nicht möglich ist.

// Derzeit macht man das etwas anders: per patch-package den export default in feature-list/grouped-content von memo(…) auf memo(withKeyboard(…)) umschreiben
// und in embed die uiListOption `scrollToItemOnPreselect` auf true setzen. fertig.


// warum gibt es diese withKeyboard:
// Firefox scrollt ein Listelement nur dann bei .scrollIntoView() in den sichtbaren Bereich, wenn das Element das den scroll hat, auch den Fokus hat
// Der Tastaturhandler wiederum funktioniert auch nur dann, wenn er auf einem Element registiert ist, das den Fokus hat
// Bei VMZNDS hat ein Element außerhalb der Liste den Scroll, weshalb es nicht funktioniert wenn der Tastaturhandler weiter unten in der Hierarchie registriert wird:
// 		Entweder ist der Fokus bei dem äußeren Element und der Tastaturhandler erfährt die Eingaben nicht, oder es ist auf dem inneren Element und der FF scrollt nicht.
// Deshalb gilt: withKeyboard muss auf die Komponente angewendet werden, die den Scroll hat
// Diese Komponenten muss die Eigenschaften tabIndex und onMouseDown forwarden
// Außerdem muss diese Komponente für den Keyboard-Handler die filteredFeatures berechnen und übergeben.

export function withKeyboard(Component/*,  keyBoardProps*/) {
	const Wrapped = function Wrapped({enableCursor: pEC, ...props}) {
		const {filteredFeatures, tabIndex, itemProps} = props;
		const dispatch = useDispatch();
		const selectionBehavior = useSelector(listUiOptionSelectionBehaviorSelector);
		const selectionBehaviorSelection = useSelector(listUiOptionSelectionSelectionSelector);
		const selectedOnly = useSelector(listUiOptionSelectedOnlySelector);

		const view = useSelector(viewSelector);

		const scrollToItemOnPreselect = useSelector(listUiScrollToItemOnPreselectSelector);
		const enableCursor = pEC || typeof itemProps === 'undefined' || scrollToItemOnPreselect;

		// data only needed for cursor
		const featureIds = useMemo(
			() => (
				!enableCursor || !filteredFeatures ? undefined :
					filteredFeatures.map(f => f.id)
			),
			[enableCursor, filteredFeatures]
		);
		const selectionIndexSelector = useMemo(
			() => featureIds && createSelectionIndexSelector(featureIds, FEATURE_SELECTION_PRESELECT),
			[featureIds]
		);
		const selectionIndex = useSelector(state => (selectionIndexSelector ? selectionIndexSelector(state) : undefined));
		const onKeyDown = useMemo(  // yes, useMemo. as we don't want a Callback at all if it does nothing
			() => (featureIds?.length ? (ev) => handleKeyboardEvent(ev, featureIds, selectionIndex, selectedOnly, selectionBehavior, selectionBehaviorSelection, view, dispatch) : undefined),
			[featureIds, selectionIndex, selectedOnly, selectionBehavior, selectionBehaviorSelection, view, dispatch]
		);

		return /*#__PURE__*/React.createElement(Component, {
			tabIndex: tabIndex || onKeyDown ? 0 : undefined,
			onKeyDown: onKeyDown,
			...props
		});
	};

	// Format for display in DevTools
	const name = Component.displayName || Component.name || 'Unknown';
	Wrapped.displayName = `withKeyboard(${name})`;
	return Wrapped;
}


// cursor navigation helpers

const moveNext = (sourceIds, idx, dispatch) => {
	if (!sourceIds || !sourceIds.length || idx >= (sourceIds.length - 1)) {
		return;
	}
	const targetId = idx < 0 ? sourceIds[0] : sourceIds[idx + 1];
	dispatch(selectExclusively(
		FEATURE_SELECTIONS,
		FEATURE_SELECTION_PRESELECT,
		targetId
	));
};

const movePrev = (sourceIds, idx, dispatch) => {
	if (!sourceIds || !sourceIds.length || idx >= sourceIds.length || idx === 0) {
		return;
	}
	const targetId = idx < 0 ?
		sourceIds[sourceIds.length - 1] :
		sourceIds[idx - 1];
	dispatch(selectExclusively(
		FEATURE_SELECTIONS,
		FEATURE_SELECTION_PRESELECT,
		targetId
	));
};

const select = (sourceIds, idx, selectedOnly, selectionBehavior, selectionBehaviorSelection, view, dispatch) => {
	// console.log('withKeyboard select', {sourceIds, idx, selectionBehavior, view});
	if (!sourceIds || !sourceIds.length || idx >= sourceIds.length) {
		return;
	}
	const targetId = idx < 0 ?
		sourceIds[0] :
		sourceIds[idx];

	// das braucht hier keine Optimierung: createSelectFeature wird ja eh erst dann aufgerufen, wenn es tatsächlich gebraucht wird.
	(createSelectFeature(selectedOnly, selectionBehavior, selectionBehaviorSelection, view, dispatch))(targetId);
};


function handleKeyboardEvent(ev, featureIds, selectionIndex, selectedOnly, selectionBehavior, selectionBehaviorSelection, view, dispatch) {
	if (ev.key === 'ArrowUp') {
		movePrev(featureIds, selectionIndex, dispatch);
		ev.preventDefault();
	} else if (ev.key === 'ArrowDown') {
		moveNext(featureIds, selectionIndex, dispatch);
		ev.preventDefault();
	} else if (ev.key === 'Enter' && ev.target.tagName !== 'A') {
		select(featureIds, selectionIndex, selectedOnly, selectionBehavior, selectionBehaviorSelection, view, dispatch);
		ev.preventDefault();
	}
}
